<template>
  <profile-edit-section-card
    v-if="localState"
    title="Services"
    breadcrumb-label="Profile"
    :saving="isSaving"
    :has-unsaved-changes="hasUnsavedChanges"
    @breadcrumb-click="cancelEdit"
    @save="handleSave"
    @cancel="cancelEdit"
  >
    <radio-button-group
      v-model="comfortableWithPetsValue"
      name="Are you comfortable with pets?"
      :options="yesNoOptions"
      class="mb-24"
    />
    <radio-button-group
      v-model="willingToProvideTransportationValue"
      name="Are you willing to provide non-medical transportation?"
      :options="yesNoOptions"
      class="mb-24"
    />
    <radio-button-group
      v-model="willingToRunErrandsValue"
      name="Are you willing to run errands?"
      :options="yesNoOptions"
      class="mb-24"
    />
    <fieldset class="mb-24">
      <legend class="form__label !mb-0">Individualized services</legend>
      <helper-text text="Select all that apply" class="pb-16 pl-0" />
      <ul>
        <li v-for="option in individualizedServiceOptions" :key="option.value" class="mb-8">
          <poppins-checkbox v-model="localState.individualizedServices" :label-name="option.label" :value="option.value" />
        </li>
      </ul>
    </fieldset>
    <fieldset>
      <legend class="form__label !mb-0">Health & medical services</legend>
      <helper-text text="Select all that apply" class="pb-16 pl-0" />
      <ul>
        <li v-for="option in healthServiceOptions" :key="option.value" class="mb-8">
          <poppins-checkbox v-model="localState.healthAndMedicalServices" :label-name="option.label" :value="option.value" />
        </li>
      </ul>
    </fieldset>
  </profile-edit-section-card>
</template>

<script lang="ts">
import PoppinsCheckbox from '@/components/checkbox/Checkbox.vue';
import ProfileEditSectionCard from '../../components/ProfileEditSectionCard.vue';
import RadioButtonGroup from '@/components/radioButtonGroup/RadioButtonGroup.vue';
import HelperText from '@/components/helper_text/HelperText.vue';
import profileEditMixin from '../../mixins/profileEditMixin';
import {
  COMPANION_CARE_HEALTH_SERVICES_OPTIONS,
  COMPANION_CARE_INDIVIDUALIZED_SERVICES_OPTIONS,
} from '../../__types__/CareTypeFormOptions';
import type { CompanionCareProfileFormState } from '../../__types__/CompanionCareProfileTypes';
import {
  formStateToOfferedServices,
  offeredServicesFromProfile,
  offeredServicesToFormState,
} from '../../lib/companionCareServiceUtils';

export default {
  name: 'CompanionCareServicesForm',
  components: {
    ProfileEditSectionCard,
    RadioButtonGroup,
    PoppinsCheckbox,
    HelperText,
  },
  mixins: [profileEditMixin],
  data(): any {
    return {
      localState: null,
      originalState: null,
    };
  },
  computed: {
    yesNoOptions: () => [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ],
    individualizedServiceOptions: () => COMPANION_CARE_INDIVIDUALIZED_SERVICES_OPTIONS,
    healthServiceOptions: () => COMPANION_CARE_HEALTH_SERVICES_OPTIONS,
    comfortableWithPetsValue: {
      get(): string {
        return String(this.localState?.comfortableWithPets ?? false);
      },
      set(value: string) {
        if (this.localState) {
          this.localState.comfortableWithPets = value === 'true';
        }
      },
    },
    willingToProvideTransportationValue: {
      get(): string {
        return String(this.localState?.willingToProvideTransportation ?? false);
      },
      set(value: string) {
        if (this.localState) {
          this.localState.willingToProvideTransportation = value === 'true';
        }
      },
    },
    willingToRunErrandsValue: {
      get(): string {
        return String(this.localState?.willingToRunErrands ?? false);
      },
      set(value: string) {
        if (this.localState) {
          this.localState.willingToRunErrands = value === 'true';
        }
      },
    },
    hasUnsavedChanges(): boolean {
      if (!this.localState || !this.originalState) return false;
      return JSON.stringify(this.localState) !== JSON.stringify(this.originalState);
    },
  },
  watch: {
    $route() {
      this.initializeFormState();
    },
    formState: {
      handler(newState) {
        if (newState) {
          this.initializeFormState();
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.initializeFormState();
  },
  methods: {
    initializeFormState() {
      const state = this.formState as CompanionCareProfileFormState | null;
      if (!state) return;

      const offered = offeredServicesToFormState(offeredServicesFromProfile(state));
      this.localState = {
        comfortableWithPets: state.comfortableWithPets ?? false,
        ...offered,
      };
      this.originalState = JSON.parse(JSON.stringify(this.localState));
    },
    async handleSave() {
      if (!this.localState) return;

      if (this.hasUnsavedChanges) {
        await this.saveSection({
          comfortableWithPets: this.localState.comfortableWithPets,
          services: formStateToOfferedServices(this.localState),
        });
      }
    },
  },
};
</script>
